import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const CommissionContainer = styled.div`
  width: 100%;
  padding: 6rem 2rem;
  background: ${corporateTheme.backgroundGradient};
  position: relative;
  overflow: hidden;
  font-family: ${corporateTheme.fontFamily};
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.9) 100%),
              url('https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  filter: brightness(1.1) contrast(0.8);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${corporateTheme.textSecondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const CommissionTables = styled(motion.div)`
  display: grid;
  gap: 3rem;
  margin-bottom: 4rem;
`;

const TableSection = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(20px);
  border-radius: 25px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  overflow-x: auto;
  box-shadow: ${corporateTheme.cardShadow};
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

const TableTitle = styled.h3`
  font-size: 2rem;
  color: ${corporateTheme.primary};
  text-align: center;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
`;

const TableHeader = styled.thead`
  background: rgba(30, 64, 175, 0.1);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${corporateTheme.cardBorder};
  
  &:hover {
    background: rgba(30, 64, 175, 0.05);
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  color: ${corporateTheme.primary};
  font-weight: 600;
  text-align: center;
  border: 1px solid ${corporateTheme.cardBorder};
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.25rem;
    font-size: 0.8rem;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  color: ${corporateTheme.textSecondary};
  text-align: center;
  border: 1px solid ${corporateTheme.cardBorder};
  
  &.highlight {
    color: ${corporateTheme.primary};
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.25rem;
    font-size: 0.8rem;
  }
`;

const EarningHighlight = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const EarningCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  backdrop-filter: blur(20px);
  border-radius: 25px;
  padding: 3rem;
  text-align: center;
  border: 1px solid rgba(30, 64, 175, 0.2);
  position: relative;
  overflow: hidden;
  box-shadow: ${corporateTheme.cardShadow};
`;

const EarningAmount = styled.div`
  font-size: 4rem;
  font-weight: 900;
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  text-shadow: 0 0 30px rgba(30, 64, 175, 0.3);
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const EarningLabel = styled.div`
  font-size: 1.5rem;
  color: ${corporateTheme.textPrimary};
  font-weight: 700;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const EarningDescription = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
`;

const BonusStructure = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  border-radius: 25px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

const BonusTitle = styled.h3`
  font-size: 2rem;
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const BonusTiers = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const BonusTier = styled(motion.div)`
  background: rgba(30, 64, 175, 0.05);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  border: 1px solid rgba(30, 64, 175, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(30, 64, 175, 0.1);
    transform: translateY(-5px);
  }
`;

const TierVolume = styled.div`
  font-size: 1.5rem;
  color: ${corporateTheme.primary};
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const TierBonus = styled.div`
  font-size: 2.5rem;
  color: ${corporateTheme.textPrimary};
  font-weight: 900;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const TierDescription = styled.div`
  color: ${corporateTheme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CommissionStructure = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const ecommerceCommissions = [
    { customers: "1-10", revShare: "30%", cpa: "€200", bonus: "-" },
    { customers: "11-25", revShare: "35%", cpa: "€250", bonus: "€1,000" },
    { customers: "26-50", revShare: "40%", cpa: "€300", bonus: "€2,500" },
    { customers: "51+", revShare: "45%", cpa: "€400", bonus: "€5,000" }
  ];

  const saasCommissions = [
    { customers: "1-15", cpa: "€150", bonus: "-" },
    { customers: "16-30", cpa: "€200", bonus: "€800" },
    { customers: "31-60", cpa: "€300", bonus: "€2,000" },
    { customers: "61+", cpa: "€400", bonus: "€4,000" }
  ];

  return (
    <CommissionContainer ref={ref} id="stats">
      <BackgroundImage />
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Header>
            <Title variants={itemVariants}>
              {corporateLabels.commissions} Structure
            </Title>
            <Subtitle variants={itemVariants}>
              Transparent, tier-based commissions designed to reward your success
            </Subtitle>
          </Header>

          <EarningHighlight variants={itemVariants}>
            <EarningCard>
              <EarningAmount>45%</EarningAmount>
              <EarningLabel>Maximum {corporateLabels.revenue} Share</EarningLabel>
              <EarningDescription>
                Highest tier commission for e-commerce revenue sharing programs
              </EarningDescription>
            </EarningCard>

            <EarningCard>
              <EarningAmount>€5K</EarningAmount>
              <EarningLabel>Monthly Bonus</EarningLabel>
              <EarningDescription>
                Additional bonus payments for high-volume affiliates
              </EarningDescription>
            </EarningCard>

            <EarningCard>
              <EarningAmount>€400</EarningAmount>
              <EarningLabel>Maximum CPA</EarningLabel>
              <EarningDescription>
                Per qualified customer for SaaS and digital product campaigns
              </EarningDescription>
            </EarningCard>
          </EarningHighlight>

          <CommissionTables variants={itemVariants}>
            <TableSection>
              <TableTitle>E-COMMERCE {corporateLabels.revenue} Share & CPA</TableTitle>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Monthly Qualified Customers</TableHeaderCell>
                    <TableHeaderCell>Revenue Share</TableHeaderCell>
                    <TableHeaderCell>CPA Option</TableHeaderCell>
                    <TableHeaderCell>Monthly Bonus</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {ecommerceCommissions.map((tier, index) => (
                    <TableRow key={index}>
                      <TableCell className="highlight">{tier.customers}</TableCell>
                      <TableCell className="highlight">{tier.revShare}</TableCell>
                      <TableCell className="highlight">{tier.cpa}</TableCell>
                      <TableCell>{tier.bonus}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
              <EarningDescription>
                * Minimum purchase €50, qualified customer retention for 30 days. Bonus paid when monthly net sales exceed 2.5x commission total.
              </EarningDescription>
            </TableSection>

            <TableSection>
              <TableTitle>SaaS & Digital Services CPA</TableTitle>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Monthly Qualified Customers</TableHeaderCell>
                    <TableHeaderCell>CPA Commission</TableHeaderCell>
                    <TableHeaderCell>Monthly Bonus</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {saasCommissions.map((tier, index) => (
                    <TableRow key={index}>
                      <TableCell className="highlight">{tier.customers}</TableCell>
                      <TableCell className="highlight">{tier.cpa}</TableCell>
                      <TableCell>{tier.bonus}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
              <EarningDescription>
                * Minimum subscription €100, minimum 3 months retention. Customer must remain active for 30 days.
              </EarningDescription>
            </TableSection>
          </CommissionTables>

          <BonusStructure variants={itemVariants}>
            <BonusTitle>Volume Bonus Structure</BonusTitle>
            <BonusTiers>
              <BonusTier variants={itemVariants} whileHover={{ scale: 1.05 }}>
                <TierVolume>25+ Customers</TierVolume>
                <TierBonus>€1K</TierBonus>
                <TierDescription>Additional monthly bonus for consistent performers</TierDescription>
              </BonusTier>

              <BonusTier variants={itemVariants} whileHover={{ scale: 1.05 }}>
                <TierVolume>50+ Customers</TierVolume>
                <TierBonus>€2.5K</TierBonus>
                <TierDescription>Mid-tier bonus for growing affiliate businesses</TierDescription>
              </BonusTier>

              <BonusTier variants={itemVariants} whileHover={{ scale: 1.05 }}>
                <TierVolume>100+ Customers</TierVolume>
                <TierBonus>€5K</TierBonus>
                <TierDescription>Premium bonus for top-performing affiliate partners</TierDescription>
              </BonusTier>
            </BonusTiers>
          </BonusStructure>
        </motion.div>
      </Container>
    </CommissionContainer>
  );
};

export default CommissionStructure; 